/**
 * Which traffic an analytics event belongs to ([[BUGS #527]]).
 *
 * Production, staging, PR previews and local builds all report into the one
 * analytics property (they share the Firebase web app), and every automated
 * browser test counted as a new visitor — ~6k "users" a month from CI alone.
 * They are still tracked (owner, 2026-10-25: "it is ok to track staging,
 * tests, but … don't mix with prod"), but every event carries:
 *
 *  - `app_env` — the build's environment, or `local`;
 *  - `automated` — `yes` for a browser driven by a test (Playwright, Selenium);
 *  - `traffic_type: internal` — on everything that is not a real visitor on
 *    the production site. This is the parameter the analytics property's
 *    "Internal traffic" data filter reads, so one filter separates them.
 *
 * Pure: the caller passes what it read from the build and the browser.
 */

export interface AnalyticsTraffic {
  appEnv: string;
  automated: boolean;
  /** True only for a person on the production site — the numbers to report. */
  isProduction: boolean;
  /** Sent with every event (`setDefaultEventParameters`). */
  params: Record<string, string>;
}

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0', '[::1]'];

export function analyticsTraffic(
  buildEnv: string | undefined,
  hostname: string | undefined,
  webdriver: boolean | undefined,
): AnalyticsTraffic {
  const local = LOCAL_HOSTS.includes(String(hostname || '').toLowerCase());
  // A production bundle served from a laptop is still a laptop.
  const appEnv = local ? 'local' : String(buildEnv || '').trim() || 'unknown';
  const automated = webdriver === true;
  const isProduction = appEnv === 'production' && !automated;
  return {
    appEnv,
    automated,
    isProduction,
    params: {
      app_env: appEnv,
      automated: automated ? 'yes' : 'no',
      ...(isProduction ? {} : { traffic_type: 'internal' }),
    },
  };
}

/** The traffic of the page this bundle is running in. */
export function currentAnalyticsTraffic(): AnalyticsTraffic {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  return analyticsTraffic(
    process.env.REACT_APP_ENV,
    typeof window !== 'undefined' ? window.location.hostname : undefined,
    nav ? Boolean((nav as Navigator & { webdriver?: boolean }).webdriver) : undefined,
  );
}
